export type AvatarListKind = 'avatar' | 'oc' | 'spineAvatar'

export type AvatarListItem = {
  id: string
  listType?: AvatarListKind
  name?: string
  authorName?: string
  url?: string
  avatar?: string
  videoUrl?: string
  videoPreviewUrl?: string
  engine?: string
  reCreateCount?: number
  dailyReCreateCount?: number
}

export function resolveAvatarListType(type: string | undefined): string {
  const trimmed = type?.trim()
  if (!trimmed) return '100'
  return trimmed
}

export function avatarListIncludesOc(type: string | undefined): boolean {
  const resolved = resolveAvatarListType(type)
  return resolved.length >= 2 && resolved[1] === '1'
}

export function avatarListIncludesSpine(type: string | undefined): boolean {
  const resolved = resolveAvatarListType(type)
  return resolved.length >= 3 && resolved[2] === '1'
}

export function avatarNewListIsMixed(type: string | undefined): boolean {
  return avatarListIncludesOc(type) || avatarListIncludesSpine(type)
}

export function avatarDailyListIsMixed(type: string | undefined): boolean {
  return avatarListIncludesSpine(type)
}

export function getAvatarListItemKind(item: AvatarListItem): AvatarListKind {
  if (item.listType) return item.listType
  if (item.avatar) return 'oc'
  if (item.engine === 'spine' || item.videoUrl || item.videoPreviewUrl) {
    return 'spineAvatar'
  }
  return 'avatar'
}

export function buildAvatarListAfterCursor(
  item: AvatarListItem,
  type: string | undefined,
  listMode: 'new' | 'daily',
): string {
  const isMixed =
    listMode === 'new'
      ? avatarNewListIsMixed(type)
      : avatarDailyListIsMixed(type)
  if (!isMixed) return item.id
  return `${getAvatarListItemKind(item)}-${item.id}`
}

export function parseAvatarListLimit(
  query: Record<string, string> | undefined,
): number {
  const raw = query?.limit
  const parsed = raw ? Number.parseInt(raw, 10) : 10
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 10
}

function normalizeAvatarListItem(raw: Record<string, unknown>): AvatarListItem {
  const rawType = raw.type
  const listType =
    rawType === 'avatar' || rawType === 'oc' || rawType === 'spineAvatar'
      ? rawType
      : undefined
  const dailyCount = raw.dailyCount
  const dailyReCreateCount =
    dailyCount &&
    typeof dailyCount === 'object' &&
    typeof (dailyCount as { reCreateCount?: unknown }).reCreateCount ===
      'number'
      ? ((dailyCount as { reCreateCount: number }).reCreateCount as number)
      : undefined

  return {
    id: String(raw.id),
    listType,
    name: typeof raw.name === 'string' ? raw.name : undefined,
    authorName: typeof raw.authorName === 'string' ? raw.authorName : undefined,
    url: typeof raw.url === 'string' ? raw.url : undefined,
    avatar: typeof raw.avatar === 'string' ? raw.avatar : undefined,
    videoUrl: typeof raw.videoUrl === 'string' ? raw.videoUrl : undefined,
    videoPreviewUrl:
      typeof raw.videoPreviewUrl === 'string' ? raw.videoPreviewUrl : undefined,
    engine: typeof raw.engine === 'string' ? raw.engine : undefined,
    reCreateCount:
      typeof raw.reCreateCount === 'number' ? raw.reCreateCount : undefined,
    dailyReCreateCount,
  }
}

export function parseAvatarListResponse(data: unknown): AvatarListItem[] {
  if (!data) return []
  if (Array.isArray(data)) {
    return data
      .filter(
        (item): item is Record<string, unknown> =>
          item != null && typeof item === 'object' && typeof item.id === 'string',
      )
      .map(normalizeAvatarListItem)
  }
  if (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as { data?: unknown }).data)
  ) {
    return parseAvatarListResponse((data as { data: unknown }).data)
  }
  return []
}
